import { Link } from 'react-router-dom';
import { Mail, Clock, FileText, Shield, Users } from 'lucide-react';
import SEO from '@/components/SEO';

const SUPPORT_EMAIL = 'support@opencardvault.com';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="Contact Us"
        description="Get in touch with the Open Card Vault support team. We're here to help with any questions about buying, selling, or trading cards."
        url="/contact"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <Mail className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">Contact Us</h1>
          </div>
          <p className="text-brand-100">We're here to help</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-2xl">
        <div className="space-y-6">

          {/* Main contact card */}
          <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-4">Get in Touch</h2>
            <p className="text-gray-700 dark:text-zinc-300 mb-6 leading-relaxed">
              Have a question, issue, or feedback? Send us an email and our support team
              will get back to you as soon as possible.
            </p>

            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="btn-primary inline-flex items-center gap-2 text-base py-3 px-6"
            >
              <Mail className="h-5 w-5" />
              {SUPPORT_EMAIL}
            </a>

            <div className="mt-6 flex items-start gap-3 text-sm text-gray-500 dark:text-zinc-500">
              <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                We typically respond within <strong className="text-gray-700 dark:text-zinc-300">1–2 business days</strong>.
                Urgent issues (account security, fraud) are prioritised.
              </span>
            </div>
          </div>

          {/* Topic cards */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1">Account & Billing</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-500 mb-3">
                Login issues, payment questions, subscription or fee enquiries.
              </p>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Account%20%2F%20Billing%20Enquiry`}
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                Email support →
              </a>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1">Marketplace Disputes</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-500 mb-3">
                Order not received, item not as described, or buyer/seller conflicts.
              </p>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Marketplace%20Dispute`}
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                Email support →
              </a>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1">Report Abuse</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-500 mb-3">
                Harassment, scams, or content that violates our community guidelines.
              </p>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Report%20Abuse`}
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                Email support →
              </a>
            </div>

            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
              <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-1">Privacy Requests</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-500 mb-3">
                Data access, correction, or deletion requests under GDPR / CCPA.
              </p>
              <a href="mailto:privacy@opencardvault.com?subject=Privacy%20Request"
                className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                Email privacy team →
              </a>
            </div>
          </div>

          {/* Legal links */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-6">
            <h3 className="font-semibold text-gray-900 dark:text-zinc-100 mb-4">Legal</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <Shield className="h-4 w-4 text-brand-500 flex-shrink-0" />
                <Link to="/legal/privacy" className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                  Privacy Policy
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <FileText className="h-4 w-4 text-brand-500 flex-shrink-0" />
                <Link to="/legal/terms" className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                  Terms of Service
                </Link>
              </li>
              <li className="flex items-center gap-3">
                <Users className="h-4 w-4 text-brand-500 flex-shrink-0" />
                <Link to="/legal/community-guidelines" className="text-sm text-brand-600 dark:text-brand-400 hover:underline">
                  Community Guidelines
                </Link>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
}
